import { TreeNodeData } from "@asup/tree-of-nodes";
import { MosaicTocEntryDto } from "contexts/toc/interface";
import { MosaicTocSectionClass } from "contexts/toc/MosaicTocSectionClass";
import { tocSort } from "contexts/toc/tocSort";

/**
 * Build a tree-of-nodes list from a mixture of TOC sections and entries.
 *
 * @param tocItems - Array of `MosaicTocSectionClass` or entry DTOs to convert.
 * @param filter - Optional text filter used to restrict returned entries by label.
 * @returns Array of `TreeNodeData` suitable for the tree component.
 */
export const getTreeListFromTocItems = (
  tocItems: (MosaicTocSectionClass | MosaicTocEntryDto)[],
  filter: string = "",
): TreeNodeData<string | null>[] => {
  let ret: TreeNodeData<string | null>[] = [];
  // Get all sections
  let lastSection = "__NOPE!!__";
  let lastSectionTocNumber = "__NOPE!!__";

  const sections = tocItems.filter((n) => n instanceof MosaicTocSectionClass);
  const sectionNodes = sections
    .sort((a, b) => tocSort(a.tocNumber, b.tocNumber))
    .map((n) => {
      const node: TreeNodeData<null> = {
        id: n.id,
        label: `${n.tocNumber} ${n.tocLabel}`,
        parentId: n.tocNumber.startsWith(lastSectionTocNumber) ? lastSection : undefined,
        data: null,
      } as TreeNodeData<null>;
      lastSection = n.id;
      lastSectionTocNumber = n.tocNumber;
      return node;
    });

  // Assign entry to the section that matches the most characters at the start of tocNumber
  const entryNodes = tocItems
    .filter((n) => !(n instanceof MosaicTocSectionClass))
    .map((n) => {
      const parentSection = sections
        .filter((s) =>
          (
            n.tocNumber ??
            // istanbul ignore next
            ""
          ).startsWith(
            s.tocNumber ??
              // istanbul ignore next
              "",
          ),
        )
        .sort((a, b) =>
          tocSort(
            a.tocNumber ??
              // istanbul ignore next
              "",
            b.tocNumber ??
              // istanbul ignore next
              "",
          ),
        )
        .pop();
      const node: TreeNodeData<string> = {
        id: n.id,
        label: `${n.tocNumber} ${n.tocLabel}`,
        data:
          n.tocNumber ??
          // istanbul ignore next
          "",
        parentId: parentSection ? parentSection.id : undefined,
      };
      return node;
    });

  // Filter entries based on the provided filter
  if (filter) {
    const filterLower = filter.toLowerCase();
    const filteredEntries = entryNodes.filter((n) => n.label.toLowerCase().includes(filterLower));
    // Add sections for the filtered entries recursively for parentIds
    const filteredSections = sectionNodes.filter(
      (s) =>
        filteredEntries.some((e) => e.parentId === s.id) ||
        s.label.toLowerCase().includes(filterLower),
    );
    while (
      filteredSections.some(
        (s) => s.parentId && !filteredSections.map((fs) => fs.id).includes(s.parentId),
      )
    ) {
      const missingNodes = sectionNodes.filter((s) =>
        filteredSections.map((fs) => fs.parentId).includes(s.id),
      );
      filteredSections.push(...missingNodes);
    }

    // Combine filtered sections and entries
    ret = [...filteredSections, ...filteredEntries];
  }
  // Combine sections and entries
  else {
    ret = [...sectionNodes, ...entryNodes];
  }
  return ret.sort((a, b) => {
    return tocSort(a.label.split(" ")[0], b.label.split(" ")[0]);
  });
};
